'use client'

import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
} from 'framer-motion'
import { cn } from '@/lib/utils'

const MOTION_EASE = [0.22, 1, 0.36, 1] as const

type TypewriterPhase = 'typing' | 'holding' | 'deleting' | 'between'

/** Natural per-character delay: spaces and punctuation breathe a little */
const getTypewriterDelay = (char: string, phase: TypewriterPhase) => {
  if (phase === 'holding') return 0
  if (phase === 'between') return 360
  if (phase === 'deleting') return 22

  if (char === '.') return 140
  if (char === ' ') return 78
  return 46
}

/** Type, pause, delete, next word: ref-driven for smooth timing */
const useNinxlTypewriter = (words: readonly string[], pauseMs: number, enabled: boolean) => {
  const [text, setText] = useState('')
  const loopRef = useRef({
    wordIndex: 0,
    charIndex: 0,
    phase: 'typing' as TypewriterPhase,
  })

  useEffect(() => {
    if (!enabled || words.length === 0) return

    let timeoutId = 0

    const schedule = (delay: number) => {
      timeoutId = window.setTimeout(runStep, delay)
    }

    const runStep = () => {
      const loop = loopRef.current
      const current = words[loop.wordIndex] ?? ''

      if (loop.phase === 'typing') {
        if (loop.charIndex >= current.length) {
          loop.phase = 'holding'
          schedule(pauseMs)
          return
        }

        loop.charIndex += 1
        setText(current.slice(0, loop.charIndex))
        schedule(getTypewriterDelay(current[loop.charIndex - 1] ?? '', 'typing'))
        return
      }

      if (loop.phase === 'holding') {
        loop.phase = 'deleting'
        schedule(getTypewriterDelay('', 'deleting'))
        return
      }

      if (loop.phase === 'deleting') {
        if (loop.charIndex <= 0) {
          loop.phase = 'between'
          loop.wordIndex = (loop.wordIndex + 1) % words.length
          setText('')
          schedule(getTypewriterDelay('', 'between'))
          return
        }

        loop.charIndex -= 1
        setText(current.slice(0, loop.charIndex))
        schedule(getTypewriterDelay(current[loop.charIndex] ?? '', 'deleting'))
        return
      }

      loop.phase = 'typing'
      loop.charIndex = 0
      schedule(120)
    }

    schedule(280)
    return () => window.clearTimeout(timeoutId)
  }, [enabled, pauseMs, words])

  return text
}

/** Cycling hero words: typewriter animation */
export const NinxlWordRotator = memo(function NinxlWordRotator({
  words,
  intervalMs = 2600,
  className,
  wordClassName,
}: {
  words: readonly string[]
  /** Pause after each word finishes typing */
  intervalMs?: number
  className?: string
  /** Applied to the typed word: use for gradient text (not the wrapper) */
  wordClassName?: string
}) {
  const shouldReduce = useReducedMotion()
  const typedText = useNinxlTypewriter(words, intervalMs, !shouldReduce && words.length > 1)

  if (words.length === 0) return null

  if (shouldReduce || words.length < 2) {
    return <span className={cn('ninxl-word-rotator-word', wordClassName, className)}>{words[0]}</span>
  }

  return (
    <span className={cn('ninxl-word-rotator inline-flex items-baseline text-left align-baseline', className)} aria-live="polite">
      <span className={cn('ninxl-word-rotator-word whitespace-nowrap', wordClassName)}>{typedText}</span>
      <motion.span
        className="ninxl-type-cursor ml-0.5 inline-block h-[0.88em] w-[2px] shrink-0 rounded-full bg-[color:var(--ninxl-brand-teal)]"
        aria-hidden
        animate={{ opacity: [1, 0.25, 1] }}
        transition={{ duration: 1.35, repeat: Infinity, ease: 'easeInOut' }}
      />
    </span>
  )
})

export type NinxlCatalogItem = {
  id: string
  title: string
  description: string
  stat: string
  statLabel: string
  icon: ReactNode
  accent: string
}

/** Scroll-pinned catalog: cards + copy swap as you scroll (supermemory-style) */
export const NinxlScrollCatalog = memo(function NinxlScrollCatalog({
  items,
  className,
}: {
  items: NinxlCatalogItem[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  })

  const smoothProgress = useSpring(scrollYProgress, { stiffness: 140, damping: 28 })

  useMotionValueEvent(smoothProgress, 'change', (value) => {
    const next = Math.min(items.length - 1, Math.max(0, Math.round(value * (items.length - 1))))
    setActiveIndex((prev) => (prev === next ? prev : next))
  })

  const scrollToIndex = useCallback((index: number) => {
    const el = ref.current
    if (!el) return
    const ratio = items.length <= 1 ? 0 : index / (items.length - 1)
    const top = el.offsetTop + ratio * (el.offsetHeight - window.innerHeight)
    window.scrollTo({ top, behavior: 'smooth' })
  }, [items.length])

  if (shouldReduce) {
    return (
      <div className={cn('grid gap-4 md:grid-cols-2', className)}>
        {items.map((item) => (
          <article key={item.id} className="rounded-3xl border border-slate-200 bg-white p-6">
            <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
            <p className="mt-2 text-sm text-slate-600">{item.description}</p>
          </article>
        ))}
      </div>
    )
  }

  const scrollHeight = `${Math.max(items.length * 85, 240)}vh`

  return (
    <div ref={ref} className={cn('ninxl-scroll-catalog relative', className)} style={{ height: scrollHeight }}>
      <div className="ninxl-scroll-catalog-stage sticky top-[14vh] flex h-[min(72vh,640px)] items-center">
        <div className="grid w-full gap-8 lg:grid-cols-[1fr_1.05fr] lg:items-center">
          <div className="relative min-h-[200px]">
            {items.map((item, i) => (
              <motion.div
                key={item.id}
                className="ninxl-catalog-text-card absolute inset-0 flex flex-col justify-center"
                animate={{
                  opacity: activeIndex === i ? 1 : 0,
                  y: activeIndex === i ? 0 : activeIndex > i ? -24 : 24,
                  filter: activeIndex === i ? 'blur(0px)' : 'blur(6px)',
                }}
                transition={{ duration: 0.5, ease: MOTION_EASE }}
                aria-hidden={activeIndex !== i}
              >
                <div className="mb-2 text-[10px] font-bold uppercase tracking-[2px] text-[color:var(--ninxl-brand-muted)]">
                  {String(i + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
                </div>
                <h3 className="ninxl-fresco-title text-3xl font-semibold tracking-tight md:text-4xl">{item.title}</h3>
                <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">{item.description}</p>
                <div className="mt-6">
                  <span className="text-3xl font-semibold text-[color:var(--ninxl-brand-teal)]">{item.stat}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[color:var(--ninxl-brand-muted)]">
                    {item.statLabel}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="relative mx-auto aspect-[4/3] w-full max-w-md lg:max-w-none">
            {items.map((item, i) => (
              <motion.article
                key={item.id}
                className="ninxl-catalog-card absolute inset-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-[color:var(--ninxl-brand-teal)]/8"
                animate={{
                  opacity: activeIndex === i ? 1 : 0,
                  scale: activeIndex === i ? 1 : 0.94,
                  rotateY: activeIndex === i ? 0 : activeIndex > i ? -8 : 8,
                  filter: activeIndex === i ? 'blur(0px)' : 'blur(4px)',
                }}
                transition={{ duration: 0.55, ease: MOTION_EASE }}
                style={{ transformPerspective: 1200 }}
                aria-hidden={activeIndex !== i}
              >
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-60', item.accent)} />
                <div className="relative flex h-full flex-col justify-between">
                  <div className="inline-flex w-fit rounded-2xl border border-[color:var(--ninxl-brand-teal)]/15 bg-white/80 p-3 text-[color:var(--ninxl-brand-teal)]">
                    {item.icon}
                  </div>
                  <div>
                    <div className="text-4xl font-semibold tracking-tight text-[#134b5f]">{item.stat}</div>
                    <div className="text-xs uppercase tracking-wider text-[color:var(--ninxl-brand-muted)]">
                      {item.statLabel}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="absolute -right-1 top-1/2 hidden -translate-y-1/2 flex-col gap-2 lg:flex">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                'ninxl-catalog-progress h-8 w-1 rounded-full transition-all duration-300',
                activeIndex === i ? 'bg-[color:var(--ninxl-brand-teal)]' : 'bg-slate-200',
              )}
              aria-label={`View ${item.title}`}
              onClick={() => scrollToIndex(i)}
            />
          ))}
        </div>
      </div>
    </div>
  )
})

/** Subtle cursor-follow parallax for hero mock */
export const NinxlMouseParallax = memo(function NinxlMouseParallax({
  children,
  className,
  strength = 12,
}: {
  children: ReactNode
  className?: string
  strength?: number
}) {
  const shouldReduce = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const target = useRef({ x: 0, y: 0 })
  const current = useRef({ x: 0, y: 0 })
  const raf = useRef<number | null>(null)

  const onMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    target.current.x = (event.clientX - rect.left) / rect.width - 0.5
    target.current.y = (event.clientY - rect.top) / rect.height - 0.5
  }, [])

  const onLeave = useCallback(() => {
    target.current.x = 0
    target.current.y = 0
  }, [])

  useEffect(() => {
    if (shouldReduce) return
    const el = ref.current
    if (!el) return

    const tick = () => {
      current.current.x += (target.current.x - current.current.x) * 0.06
      current.current.y += (target.current.y - current.current.y) * 0.06
      el.style.transform = `translate(${current.current.x * strength}px, ${current.current.y * strength * 0.7}px)`
      raf.current = requestAnimationFrame(tick)
    }

    raf.current = requestAnimationFrame(tick)
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [shouldReduce, strength])

  return (
    <div ref={ref} className={cn('ninxl-mouse-parallax', className)} onMouseMove={onMove} onMouseLeave={onLeave}>
      {children}
    </div>
  )
})

/** Blur + lift reveal on scroll */
export const NinxlBlurReveal = memo(function NinxlBlurReveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode
  className?: string
  delay?: number
}) {
  const shouldReduce = useReducedMotion()

  if (shouldReduce) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 28, filter: 'blur(10px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-64px' }}
      transition={{ duration: 0.7, delay, ease: MOTION_EASE }}
    >
      {children}
    </motion.div>
  )
})